/**
 * The follow camera over terrain relief (`q-follow-camera-vs-relief`, AD-820).
 *
 * The orbit boom's pitch is measured from the WORLD horizontal, so on a hillside
 * the same pitch looks straight into the slope: on the AD-408 consumer's verdant
 * frame the ground ahead filled ~80 % of the shot and hid the forest the flat
 * frame showed (client-terrain-consumer.verify.json). The fix is the camera, not
 * the lane's floor: where the ground ahead RISES the render pitch is lowered by
 * that rise, so an upslope frames the way flat ground does; then the pitch is
 * raised where the hill behind would put the lens in (or the sightline through)
 * the ground.
 *
 * Same contract as the dungeon run's lift (AD-706, `dungeon/runCamera.ts`): the
 * player's own `pitch` / `dist` are never written, the render pitch stays inside
 * CAMERA_BOUNDS, and it is eased so a frame never snaps. On flat ground every
 * function here returns the player's own pitch exactly. Pure; frontend-local.
 */

import { CAMERA_BOUNDS, orbitPose } from './camera.js';

/** Presentation tuning, not bounds. */
export const TERRAIN_CAMERA = {
  /** Distances ahead of the player (m) the view's terrain horizon is sampled at. */
  AHEAD_SAMPLES_M: [3, 6, 9, 12, 16] as readonly number[],
  /** Share of the ahead-horizon angle taken off the pitch (1 = full slope-relative). */
  SLOPE_FOLLOW: 1,
  /** The lens and its sightline keep at least this much air above the ground (m). */
  CLEARANCE_M: 0.8,
  /** Sightline samples between the look point and the lens. */
  SIGHT_SAMPLES: 8,
  /** Lift search step (rad); eased, so the step never shows. */
  PITCH_STEP: 0.05,
  /** Ease rate of the render pitch, per second (≈ 95 % settled in ~0.75 s). */
  PITCH_EASE_PER_S: 4,
} as const;

export type HeightAt = (x: number, z: number) => number;

interface Boom {
  yaw: number;
  pitch: number;
  dist: number;
}

/**
 * The elevation angle (rad) of the terrain horizon ahead of the player, along the
 * camera's facing: the steepest sight angle from the ground under the player to
 * the ground at each sample distance. Positive = rising ground ahead, negative =
 * falling away; exactly 0 on flat ground.
 */
export function aheadHorizonAngle(
  target: { x: number; z: number },
  yaw: number,
  heightAt: HeightAt,
): number {
  const h0 = heightAt(target.x, target.z);
  const fx = Math.sin(yaw);
  const fz = Math.cos(yaw);
  let best = -Infinity;
  for (const d of TERRAIN_CAMERA.AHEAD_SAMPLES_M) {
    const h = heightAt(target.x + fx * d, target.z + fz * d);
    if (!Number.isFinite(h)) continue;
    const a = Math.atan2(h - h0, d);
    if (a > best) best = a;
  }
  return Number.isFinite(best) ? best : 0;
}

/** True when the lens and the sightline to the look point both clear the ground. */
export function boomClearsGround(
  target: { x: number; y: number; z: number },
  boom: Boom,
  heightAt: HeightAt,
): boolean {
  const pose = orbitPose(target, boom.yaw, boom.pitch, boom.dist);
  const n = TERRAIN_CAMERA.SIGHT_SAMPLES;
  // i = 1 … n: from just off the look point out to the lens itself.
  for (let i = 1; i <= n; i++) {
    const t = i / n;
    const x = pose.lookX + (pose.x - pose.lookX) * t;
    const y = pose.lookY + (pose.y - pose.lookY) * t;
    const z = pose.lookZ + (pose.z - pose.lookZ) * t;
    if (y < heightAt(x, z) + TERRAIN_CAMERA.CLEARANCE_M) return false;
  }
  return true;
}

/**
 * The render pitch this frame over `heightAt`: the player's pitch lowered by the
 * rise of the terrain ahead (never steepened for a fall), then lifted until the boom clears the ground (up to
 * MAX_PITCH). Within CAMERA_BOUNDS always; the player's pitch on flat ground.
 * `target` is the avatar's FEET (the `orbitPose` contract).
 */
export function terrainRenderPitch(
  target: { x: number; y: number; z: number },
  orbit: Boom,
  heightAt: HeightAt,
): number {
  const { MIN_PITCH, MAX_PITCH } = CAMERA_BOUNDS;
  const horizon = aheadHorizonAngle(target, orbit.yaw, heightAt);
  // Rising ground only: over falling ground the world-horizontal pitch already
  // shows the vista, and steepening it there (measured on the real origin-basin
  // bake, lane run 20261024T183115Z) traded the horizon for a top-down shot.
  const relative = orbit.pitch - TERRAIN_CAMERA.SLOPE_FOLLOW * Math.max(0, horizon);
  let pitch = Math.min(MAX_PITCH, Math.max(MIN_PITCH, relative));
  while (pitch < MAX_PITCH && !boomClearsGround(target, { ...orbit, pitch }, heightAt)) {
    pitch = Math.min(MAX_PITCH, pitch + TERRAIN_CAMERA.PITCH_STEP);
  }
  return pitch;
}

/** Ease the render pitch toward `wanted` over `dtMs` (frame-rate independent). */
export function easeTerrainPitch(current: number, wanted: number, dtMs: number): number {
  const k = 1 - Math.exp((-TERRAIN_CAMERA.PITCH_EASE_PER_S * Math.max(0, dtMs)) / 1000);
  return current + (wanted - current) * k;
}
